import { jsx, jsxs } from "react/jsx-runtime";
import { useRef, useState } from "react";
import react_intersection_observer from "@researchgate/react-intersection-observer";
import { clsx } from "clsx";
import parseClassnames from "../helpers/parseClassnames.js";
import { DEFAULT_PLACEHOLDER } from "./constants.js";
import getImgxUrl from "./getImgxUrl.js";
const optimizedImage = (src)=>`${src}&dpr=1 1x, ${src}&dpr=2 2x`;
const LazyImage = ({ placeholder, src, imgxParams, observerProps, style, imgStyle, alt, className = '', imgClassName = '', backgroundImage = '', pictureStyles = {}, customOptimizedImage = '', dark, isDark, isTransparent })=>{
    const imageRef = useRef(null);
    const [isIntersecting, setIsIntersecting] = useState(false);
    const [isResourceLoaded, setIsResourceLoaded] = useState(false);
    const handleIntersection = (event, unobserve)=>{
        if (event.isIntersecting) {
            setIsIntersecting(true);
            unobserve();
        }
    };
    const options = {
        onChange: handleIntersection,
        rootMargin: '0px 0px 300px 0px',
        disabled: isResourceLoaded,
        ...observerProps
    };
    const webpImage = getImgxUrl(src, {
        auto: 'compress',
        fm: 'webp',
        ...imgxParams
    });
    const avifImage = getImgxUrl(src, {
        auto: 'format',
        ...imgxParams
    });
    let defaultPlaceholderTheme = isDark || dark ? 'dark' : 'light';
    if (isTransparent) defaultPlaceholderTheme = 'transparent';
    const placeholderImage = placeholder || DEFAULT_PLACEHOLDER[defaultPlaceholderTheme];
    const defaultImage = getImgxUrl(src, imgxParams);
    return /*#__PURE__*/ jsx(react_intersection_observer, {
        ...options,
        children: /*#__PURE__*/ jsxs("div", {
            className: parseClassnames(className, clsx("tw-relative tw-inline-block tw-w-full tw-h-full", {
                'tw-relative': !className.includes('tw-absolute')
            })),
            style: style,
            children: [
                /*#__PURE__*/ jsx("div", {
                    className: `tw-absolute tw-inset-0 tw-z-10 ${imgClassName}`,
                    style: {
                        ...imgStyle,
                        opacity: isResourceLoaded ? 0 : 1,
                        backgroundImage: backgroundImage || `url(${placeholderImage})`,
                        backgroundSize: 'contain',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        transition: 'opacity 0.3s ease-in-out',
                        transitionDelay: '100ms',
                        willChange: isResourceLoaded ? 'unset' : 'opacity'
                    }
                }),
                isIntersecting && /*#__PURE__*/ jsxs("picture", {
                    className: "tw-absolute tw-inset-0 tw-flex tw-justify-center tw-items-center",
                    style: pictureStyles,
                    children: [
                        /*#__PURE__*/ jsx("source", {
                            srcSet: customOptimizedImage || optimizedImage(avifImage),
                            type: "image/avif"
                        }),
                        /*#__PURE__*/ jsx("source", {
                            srcSet: customOptimizedImage || optimizedImage(webpImage),
                            type: "image/webp"
                        }),
                        /*#__PURE__*/ jsx("img", {
                            ref: imageRef,
                            className: parseClassnames(imgClassName, "tw-max-w-full tw-max-h-full tw-w-auto tw-h-auto tw-block tw-object-contain"),
                            src: defaultImage,
                            alt: alt,
                            style: {
                                ...imgStyle,
                                opacity: isResourceLoaded ? 1 : 0,
                                transition: 'opacity 300ms ease-in-out'
                            },
                            onLoad: ()=>setIsResourceLoaded(true),
                            decoding: "async"
                        })
                    ]
                })
            ]
        })
    });
};
const LazyImage_LazyImage = LazyImage;
export default LazyImage_LazyImage;
